import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Code2, Server, Shield, Brain, Megaphone, Home, FolderArchive, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../i18n";
import { SERVICE_SLUGS, slugToKey, serviceDetails, ui } from "../data/serviceDetails";

const ICONS = {
    dev: Code2,
    it: Server,
    sec: Shield,
    ai: Brain,
    mkt: Megaphone,
    iot: Home,
    doc: FolderArchive,
};

export default function ServiceDetail() {
    const { slug } = useParams();
    const { lang, dict } = useI18n();
    const u = ui[lang];
    const key = slugToKey(slug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!key) return <Navigate to="/servicios" replace />;

    const detail = serviceDetails[lang][key];
    const Icon = ICONS[key] || Code2;
    const allKeys = Object.keys(serviceDetails[lang]);
    const related = allKeys.filter((k) => k !== key).slice(0, 3);
    const items = dict.services.cats.find((c) => c.key === key)?.items || [];

    return (
        <div data-testid="service-detail-page" className="bg-ink-950 text-white">
            <Navbar />
            <main>
                {/* Hero */}
                <section className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24">
                    <div className="pointer-events-none absolute inset-0 grid-bg opacity-30 [mask-image:linear-gradient(to_bottom,#000,transparent_80%)]" />
                    <div className="pointer-events-none absolute -top-40 -left-32 h-[36rem] w-[36rem] rounded-full bg-brand/20 blur-[140px]" />
                    <div className="pointer-events-none absolute top-20 right-0 h-[24rem] w-[24rem] rounded-full bg-brand-glow/10 blur-[120px]" />
                    <div className="container-page relative">
                        <Link
                            to="/servicios"
                            data-testid="service-back-link"
                            className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {u.allServices}
                        </Link>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end"
                        >
                            <div className="lg:col-span-8">
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-brand-glow">
                                    <Icon className="h-6 w-6" />
                                </div>
                                <h1
                                    data-testid="service-title"
                                    className="mt-6 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]"
                                >
                                    {detail.title}
                                </h1>
                                <p className="mt-5 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                                    {detail.tagline}
                                </p>
                            </div>
                            <div className="lg:col-span-4">
                                <div className="card-surface gradient-border p-6">
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                                        Quick scope
                                    </span>
                                    <ul className="mt-3 space-y-2">
                                        {items.slice(0, 5).map((it) => (
                                            <li key={it} className="flex items-start gap-2 text-sm text-white/80">
                                                <Sparkles className="mt-0.5 h-3.5 w-3.5 text-brand-glow shrink-0" />
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Intro */}
                <section className="pb-16 md:pb-24">
                    <div className="container-page">
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="max-w-3xl text-lg md:text-2xl font-medium text-white/85 leading-snug tracking-tight"
                        >
                            {detail.intro}
                        </motion.p>
                    </div>
                </section>

                {/* Sections */}
                <section className="pb-20 md:pb-28">
                    <div className="container-page">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden">
                            {detail.sections.map((sec, idx) => (
                                <motion.div
                                    key={sec.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                                    className="bg-ink-950 p-7 md:p-9"
                                >
                                    <span className="text-xs font-mono text-white/35">0{idx + 1}</span>
                                    <h2 className="mt-3 text-xl md:text-2xl font-semibold tracking-tight">
                                        {sec.title}
                                    </h2>
                                    <ul className="mt-5 space-y-3">
                                        {sec.items.map((it) => (
                                            <li
                                                key={it}
                                                className="flex items-start gap-3 text-sm text-white/75 leading-relaxed"
                                            >
                                                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand/15 border border-brand/40 text-brand-glow shrink-0">
                                                    <Check className="h-3 w-3" />
                                                </span>
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="pb-24 md:pb-32">
                    <div className="container-page">
                        <div className="relative overflow-hidden rounded-3xl card-surface gradient-border p-10 md:p-16 text-center">
                            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-[24rem] w-[60%] rounded-full bg-brand/20 blur-[120px]" />
                            <h2 className="relative text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl mx-auto">
                                {u.ctaTitle}
                            </h2>
                            <p className="relative mt-4 text-white/65 max-w-xl mx-auto">
                                {u.ctaSubtitle}
                            </p>
                            <Link
                                to="/#contact"
                                data-testid="service-cta-contact"
                                className="relative mt-8 inline-flex btn-primary group"
                            >
                                {u.ctaButton}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Related */}
                <section className="pb-24 md:pb-32">
                    <div className="container-page">
                        <div className="flex items-end justify-between mb-8">
                            <h3 className="text-xl md:text-2xl font-semibold tracking-tight">{u.relatedTitle}</h3>
                            <Link
                                to="/servicios"
                                className="text-xs text-white/55 hover:text-white transition-colors inline-flex items-center gap-1.5"
                            >
                                {u.allServices}
                                <ArrowRight className="h-3 w-3" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                            {related.map((rk) => {
                                const RIcon = ICONS[rk] || Code2;
                                const rd = serviceDetails[lang][rk];
                                return (
                                    <Link
                                        key={rk}
                                        to={`/servicios/${SERVICE_SLUGS[rk]}`}
                                        data-testid={`related-service-${rk}`}
                                        className="group card-surface p-6 block"
                                    >
                                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-brand-glow">
                                            <RIcon className="h-4 w-4" />
                                        </div>
                                        <h4 className="mt-4 text-lg font-semibold tracking-tight">{rd.title}</h4>
                                        <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-2">
                                            {rd.tagline}
                                        </p>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
